import { useState } from "react";
import { Globe, MapPin, Edit3, Trash2, Plus, Loader } from "lucide-react";
import Pagination from "../../components/Pagination";
import { useQueryClient } from "@tanstack/react-query";
import useShippingCountries from "../../hooks/useShippingCountries";
import useShippingRegions from "../../hooks/useShippingRegions";
import DeleteCountryModal from "../../modals/shippingModal/deleteCountry";
import DeleteRegionModal from "../../modals/shippingModal/deleteRegion";
import AddCountryModal from "../../modals/shippingModal/addCountry";
import AddRegionModal from "../../modals/shippingModal/addRegion";
import EditCountryModal from "../../modals/shippingModal/editCountry";
import EditRegionModal from "../../modals/shippingModal/editRegion";
import formatCurrency from "../../utils/formatCurrency";

const Shipping = () => {
  const [activeTab, setActiveTab] = useState("country");
  const [deleteCountryModalOpen, setDeleteCountryModalOpen] = useState(false);
  const [deleteRegionModalOpen, setDeleteRegionModalOpen] = useState(false);
  const [addCountryModalOpen, setAddCountryModalOpen] = useState(false);
  const [addRegionModalOpen, setAddRegionModalOpen] = useState(false);
  const [editCountryModalOpen, setEditCountryModalOpen] = useState(false);
  const [editRegionModalOpen, setEditRegionModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const queryClient = useQueryClient();
  const {
    countries,
    loading: countriesLoading,
    error: countriesError,
  } = useShippingCountries();
  const {
    regions,
    loading: regionsLoading,
    error: regionsError,
  } = useShippingRegions();

  const loading = activeTab === "country" ? countriesLoading : regionsLoading;
  const error = activeTab === "country" ? countriesError : regionsError;
  const displayData = activeTab === "country" ? countries : regions;

  const handleDeleteCountryClick = (country) => {
    setSelectedCountry(country);
    setDeleteCountryModalOpen(true);
  };

  const handleDeleteRegionClick = (region) => {
    setSelectedRegion(region);
    setDeleteRegionModalOpen(true);
  };

  const handleEditCountryClick = (country) => {
    setSelectedCountry(country);
    setEditCountryModalOpen(true);
  };

  const handleEditRegionClick = (region) => {
    setSelectedRegion(region);
    setEditRegionModalOpen(true);
  };

  const handleCountryDeleteSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["shippingCountries"] });
  };

  const handleRegionDeleteSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["shippingRegions"] });
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8 text-[#7D828D]">
      {/* --- HEADER --- */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-lg sm:text-xl font-medium text-[#0C0C0E]">
          Shipping Management
        </h1>
        <p className="font-medium text-xs sm:text-sm text-[#7D828D]">
          Manage shipping countries, regions, fees, and delivery options
        </p>
      </div>

      {/* --- TABS --- */}
      <div className="flex flex-wrap border-b border-[#EEF1F3] mb-4 sm:mb-6">
        <button
          onClick={() => setActiveTab("country")}
          className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 ${
            activeTab === "country"
              ? "border-[#D2BDA1] text-[#0C0C0E]"
              : "border-transparent text-[#7D828D] hover:text-[#0C0C0E]"
          }`}
        >
          <Globe size={18} /> Countries
        </button>
        <button
          onClick={() => setActiveTab("regions")}
          className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 ${
            activeTab === "regions"
              ? "border-[#D2BDA1] text-[#0C0C0E]"
              : "border-transparent text-[#7D828D] hover:text-[#0C0C0E]"
          }`}
        >
          <MapPin size={18} /> Regions
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-medium text-[#0C0C0E]">
          {activeTab === "country"
            ? `${countries.length} Countries`
            : `${regions.length} Regions`}
        </h2>
        <button
          onClick={() =>
            activeTab === "country"
              ? setAddCountryModalOpen(true)
              : setAddRegionModalOpen(true)
          }
          className="flex items-center gap-2 bg-[#D2BDA1] text-black text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-[#c5b49f] transition-colors active:scale-95 active:shadow-sm duration-100 w-full sm:w-auto justify-center"
        >
          <Plus size={18} /> Add{" "}
          {activeTab === "country" ? "Country" : "Region"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader size={32} className="text-[#6B7143] animate-spin" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6 border border-[#EEF1F3] rounded-lg">
          <p className="text-red-600 font-medium text-center mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-[#728550] hover:text-slate-900 font-medium text-xs sm:text-sm"
          >
            Try Again
          </button>
        </div>
      ) : displayData && displayData.length === 0 ? (
        /* --- EMPTY STATE VIEW --- */
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6 border border-[#EEF1F3] rounded-lg">
          <div className="p-1 border border-slate-300 bg-[#F8F5F1] rounded-full overflow-hidden mb-4">
            {activeTab === "country" ? (
              <Globe size={32} className="text-[#BFAC93]" />
            ) : (
              <MapPin size={32} className="text-[#BFAC93]" />
            )}
          </div>
          <h4 className="font-medium text-sm sm:text-base text-[#0C0C0E] text-center mb-2">
            No {activeTab === "country" ? "countries" : "regions"} yet
          </h4>
          <p className="text-xs sm:text-sm text-slate-400 text-center">
            Add {activeTab === "country" ? "a country" : "a region"} to get
            started.
          </p>
        </div>
      ) : (
        <>
          {/* --- TABLE --- */}
          <div className="border border-[#EEF1F3] rounded-lg overflow-x-auto">
            <table className="min-w-[600px] w-full text-left shadow-sm text-xs sm:text-sm">
              <thead className="bg-[#FBF8F6] text-[#7D828D] font-medium">
                <tr>
                  {activeTab === "country" ? (
                    <>
                      <th className="px-4 sm:px-6 py-3 sm:py-4">Country</th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4">Regions</th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4">Actions</th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 sm:px-6 py-3 sm:py-4">Region</th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4">Country ID</th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4">
                        Shipping Fee
                      </th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4">Free Above</th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4">Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF1F3]">
                {activeTab === "country"
                  ? countries.map((c) => (
                      <tr
                        key={c.id}
                        className="hover:bg-slate-50/50 transition-colors"
                      >
                        <td className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-3 font-medium text-[#0C0C0E]">
                          <Globe size={18} className="text-[#BFAC93]" />{" "}
                          {c.country}
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 text-[#7D828D]">
                          {c.regions && c.regions.length} regions
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <ActionButtons
                            onEdit={() => handleEditCountryClick(c)}
                            onDelete={() => handleDeleteCountryClick(c)}
                          />
                        </td>
                      </tr>
                    ))
                  : regions.map((r) => (
                      <tr
                        key={r.id}
                        className="hover:bg-slate-50/50 transition-colors"
                      >
                        <td className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-3 font-medium text-[#0C0C0E]">
                          <MapPin size={18} className="text-[#BFAC93]" />{" "}
                          {r.region_name}
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 text-[#7D828D]">
                          {r.shipping_country_id}
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#0C0C0E]">
                          {formatCurrency(r?.shipping_fee || 0)}
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 text-[#7D828D]">
                          {formatCurrency(r?.free_shipping_above || 0)}
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <ActionButtons
                            onEdit={() => handleEditRegionClick(r)}
                            onDelete={() => handleDeleteRegionClick(r)}
                          />
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
          {/* --- PAGINATION --- */}
          <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-3 sm:gap-0 items-center justify-between border-t border-[#EEF1F3] bg-[#FBF8F6] w-full overflow-x-auto min-w-0">
            <span className="text-xs sm:text-sm text-[#7D828D]">
              Showing 1 to {displayData?.length || 0} of{" "}
              {displayData?.length || 0}
            </span>
            <div className="w-full sm:w-auto">
              <Pagination
                currentPage={1}
                totalPages={1}
                onPageChange={() => {}}
              />
            </div>
          </div>
        </>
      )}

      {selectedCountry && (
        <DeleteCountryModal
          isOpen={deleteCountryModalOpen}
          onClose={() => setDeleteCountryModalOpen(false)}
          onDeleteSuccess={handleCountryDeleteSuccess}
          country={selectedCountry}
        />
      )}

      {selectedRegion && (
        <DeleteRegionModal
          isOpen={deleteRegionModalOpen}
          onClose={() => setDeleteRegionModalOpen(false)}
          onDeleteSuccess={handleRegionDeleteSuccess}
          region={selectedRegion}
        />
      )}

      <AddCountryModal
        isOpen={addCountryModalOpen}
        onClose={() => setAddCountryModalOpen(false)}
        onSuccess={() => {
          setAddCountryModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ["shippingCountries"] });
        }}
      />

      {selectedCountry && (
        <EditCountryModal
          isOpen={editCountryModalOpen}
          onClose={() => setEditCountryModalOpen(false)}
          onSuccess={() => {
            setEditCountryModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ["shippingCountries"] });
          }}
          country={selectedCountry}
        />
      )}

      {selectedRegion && (
        <EditRegionModal
          isOpen={editRegionModalOpen}
          onClose={() => setEditRegionModalOpen(false)}
          onSuccess={() => {
            setEditRegionModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ["shippingRegions"] });
          }}
          region={selectedRegion}
        />
      )}

      <AddRegionModal
        isOpen={addRegionModalOpen}
        onClose={() => setAddRegionModalOpen(false)}
        onSuccess={() => {
          setAddRegionModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ["shippingRegions"] });
        }}
      />
    </div>
  );
};

// Reusable Action Buttons Component
const ActionButtons = ({ onEdit, onDelete }) => (
  <div className="flex gap-2 sm:gap-4">
    <button
      onClick={onEdit}
      className="flex items-center gap-1 text-[#728550] hover:text-slate-900 font-medium text-xs sm:text-sm active:scale-95 transition-transform duration-100"
    >
      <Edit3 size={16} /> Edit
    </button>
    <button
      onClick={onDelete}
      className="flex items-center gap-1 text-red-600 hover:text-red-700 font-medium text-xs sm:text-sm active:scale-95 transition-transform duration-100"
    >
      <Trash2 size={16} /> Delete
    </button>
  </div>
);

export default Shipping;
